import { useEffect, useState, type MouseEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { Clock } from 'lucide-react'
import type { LDFlags } from '@/config/launchdarkly'
import { authService } from '@/services/authService'
import { useAuthStore } from '@/stores/authStore'
import { sessionDetailsService, SessionDetails } from '@/services/sessionDetailsService'
import { parseWaitingListResponse, waitingListService, WaitingListEntry } from '@/services/waitingListService'
import {
  formatWaitlistSchedule,
  getWaitlistCountdown,
  isVisibleWaitlistOffer,
  isWaitlistNotified,
  notifiedWaitlistExpiration,
} from '@/lib/waitlistExpiration'

type NoticeProps = {
  entry: WaitingListEntry
  details?: SessionDetails
  variant?: 'notice' | 'compact'
  onCancelled?: (entry: WaitingListEntry) => void
}

export function WaitlistExpirationNotice({ entry, details, variant = 'notice', onCancelled }: NoticeProps) {
  const { t, i18n } = useTranslation('profile')
  const flags = useFlags<LDFlags & { 'show-waitlist-expiration'?: boolean }>()
  const showWaitlistExpiration = flags.showWaitlistExpiration || flags['show-waitlist-expiration'] || false
  const navigate = useNavigate()
  const [now, setNow] = useState(() => new Date())
  const [cancelling, setCancelling] = useState(false)
  const [cancelError, setCancelError] = useState<string | null>(null)

  const expiration = notifiedWaitlistExpiration(entry)
  const expirationMs = expiration?.getTime() ?? null

  useEffect(() => {
    if (expirationMs == null || expirationMs <= Date.now()) return
    const timer = window.setInterval(() => setNow(new Date()), 30_000)
    return () => window.clearInterval(timer)
  }, [expirationMs])

  if (!showWaitlistExpiration || !isVisibleWaitlistOffer(entry, now)) {
    return null
  }

  const countdown = expiration ? getWaitlistCountdown(expiration, now) : null
  const { schedule, location } = formatWaitlistSchedule(details?.schedule, i18n.language)
  const title = details?.title || details?.basic?.subTitle || t('regHistSessionNumber', { id: entry.sessionId })
  const expirationLabel = expiration?.toLocaleString(i18n.language, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }) ?? ''
  const remainingLabel = !countdown
    ? ''
    : countdown.expired
      ? t('waitlistOfferExpired', { date: expirationLabel })
      : countdown.underOneMinute
        ? t('waitlistOfferUnderOneMinute')
        : t('waitlistOfferCountdown', { hours: countdown.hours, minutes: countdown.minutes })

  const cancelSpot = async (event: MouseEvent) => {
    event.stopPropagation()
    if (!entry.key || cancelling) return
    setCancelling(true)
    setCancelError(null)
    try {
      await waitingListService.removeFromWaitingList(entry.key)
      onCancelled?.(entry)
    } catch {
      setCancelError(t('waitlistOfferCancelFailed'))
    } finally {
      setCancelling(false)
    }
  }

  const register = (event: MouseEvent) => {
    event.stopPropagation()
    navigate(`/session/${entry.sessionId}`)
  }

  return (
    <div
      className={variant === 'compact'
        ? 'mt-4 rounded-lg border border-amber-300 bg-amber-50 p-4'
        : 'rounded-xl border border-amber-300 bg-amber-50 p-4 sm:p-5'}
      data-testid="waitlist-expiration-notice"
    >
      {variant === 'notice' && isWaitlistNotified(entry) && (
        <p className="text-sm text-amber-950">{t('waitlistOfferIntro')}</p>
      )}
      <div className={`${variant === 'notice' ? 'mt-3' : ''} grid grid-cols-1 gap-2 text-sm text-amber-950 sm:grid-cols-2`}>
        <p><span className="font-semibold">{t('waitlistOfferSessionId')}:</span> {entry.sessionId}</p>
        <p><span className="font-semibold">{t('waitlistOfferTitle')}:</span> {title}</p>
        {schedule && <p><span className="font-semibold">{t('waitlistOfferSchedule')}:</span> {schedule}</p>}
        {location && <p><span className="font-semibold">{t('waitlistOfferLocation')}:</span> {location}</p>}
        {countdown && (
          <>
            <p><span className="font-semibold">{t('waitlistOfferExpires')}:</span> {expirationLabel}</p>
            <p className="inline-flex items-center gap-1 font-semibold">
              <Clock className="h-4 w-4" aria-hidden="true" />
              {countdown.expired ? remainingLabel : `${t('waitlistOfferRemaining')}: ${remainingLabel}`}
            </p>
          </>
        )}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={register}
          className="rounded-lg px-3 py-2 text-sm font-semibold text-white"
          style={{ backgroundColor: 'var(--color-primary)' }}
        >
          {t('waitlistOfferRegister')}
        </button>
        {entry.key != null && (
          <button
            type="button"
            onClick={cancelSpot}
            disabled={cancelling}
            className="rounded-lg border border-amber-700 px-3 py-2 text-sm font-semibold text-amber-950 disabled:opacity-60"
          >
            {cancelling ? t('waitlistOfferCancelling') : t('waitlistOfferCancel')}
          </button>
        )}
      </div>
      {cancelError && <p className="mt-2 text-sm text-red-700">{cancelError}</p>}
    </div>
  )
}

type NoticesProps = {
  entries?: WaitingListEntry[]
  sessionDetails?: Record<number, SessionDetails>
  onCancelled?: () => void
}

export function WaitlistExpirationNotices({ entries, sessionDetails, onCancelled }: NoticesProps) {
  const { t } = useTranslation('profile')
  const flags = useFlags<LDFlags & { 'show-waitlist-expiration'?: boolean }>()
  const showWaitlistExpiration = flags.showWaitlistExpiration || flags['show-waitlist-expiration'] || false
  const { user } = useAuthStore()
  const controlled = entries !== undefined
  const [loadedEntries, setLoadedEntries] = useState<WaitingListEntry[]>([])
  const [loadedDetails, setLoadedDetails] = useState<Record<number, SessionDetails>>({})
  const [now] = useState(() => new Date())

  useEffect(() => {
    if (controlled || !showWaitlistExpiration) return

    const userId = authService.getLegacyUserpk() || (user?.legacyUserpk ?? 0)
    if (!userId) return

    let cancelled = false
    waitingListService.getWaitingListForUser(userId)
      .then(async raw => {
        const active = parseWaitingListResponse(raw).entries.filter(entry =>
          isVisibleWaitlistOffer(entry, new Date())
        )
        if (cancelled) return
        setLoadedEntries(active)
        const details = await Promise.allSettled(
          active.map(async entry => ({
            sessionId: Number(entry.sessionId),
            details: await sessionDetailsService.getSessionDetails(Number(entry.sessionId)),
          }))
        )
        if (cancelled) return
        setLoadedDetails(Object.fromEntries(
          details.flatMap(result => result.status === 'fulfilled' ? [[result.value.sessionId, result.value.details]] : [])
        ))
      })
      .catch(() => {
        if (!cancelled) setLoadedEntries([])
      })

    return () => {
      cancelled = true
    }
  }, [controlled, showWaitlistExpiration, user?.legacyUserpk])

  if (!showWaitlistExpiration) return null

  const source = controlled ? entries : loadedEntries
  const details = controlled ? sessionDetails : loadedDetails
  const offers = source.filter(entry => isVisibleWaitlistOffer(entry, now))
  if (offers.length === 0) return null

  return (
    <section className="mb-6 space-y-3" aria-label={t('waitlistOfferNoticesHeading')}>
      <h2 className="text-base font-semibold text-amber-950">{t('waitlistOfferNoticesHeading')}</h2>
      {offers.map(entry => (
        <WaitlistExpirationNotice
          key={entry.key ?? `${entry.userId}-${entry.sessionId}`}
          entry={entry}
          details={details?.[Number(entry.sessionId)]}
          onCancelled={() => {
            if (!controlled) {
              setLoadedEntries(current => current.filter(item => item.key !== entry.key))
            }
            onCancelled?.()
          }}
        />
      ))}
    </section>
  )
}
